import {
  ApplicationConfig,
  CSP_NONCE,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import {
  ExtraOptions,
  provideRouter,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withRouterConfig,
} from '@angular/router';
import { firstValueFrom, race, timer } from 'rxjs';
import { map } from 'rxjs/operators';
import { routes } from './app.routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideHttpClient,
  withInterceptors,
  withXhr,
} from '@angular/common/http';
import { AuthModule } from './auth/auth.module';
import { AuthenticationService } from './shared/services/authentication/authentication.service';
import {
  tokenInterceptor,
  errorInterceptor,
  loadingInterceptor,
  assetCacheInterceptor,
} from './shared/interceptors';
import { ToastrModule } from 'ngx-toastr';
import { AlertModule } from 'ngx-bootstrap/alert';
import { ModalModule } from 'ngx-bootstrap/modal';

import { LocalforageService } from './shared/services/localforage/localforage.service';
import { PermissionsService } from './shared/services/permissions/permissions.service';
import { DeploymentInfoService } from './shared/services/deployment-info/deployment-info.service';
import * as Sentry from '@sentry/angular';
import { DuckDbService } from './shared/services/duck-db/duck-db.service';
import { JavascriptService } from './shared/services/javascript/javascript.service';
import { PythonService } from './shared/services/python/python.service';
import { Sam2WebsocketService } from './shared/services/sam2-websocket/sam2-websocket.service';
import { NotificationWebsocketService } from './shared/services/notification-websocket/notification-websocket.service';
import {
  APP_STATE_SERVICE,
  AppStateService,
  PROJECT_SERVICE,
} from './shared/services/app-state/app-state.service';
import { ProjectService } from './shared/services/project/project.service';
import { IconRegistryService } from './shared/services/icon-registry/icon-registry.service';
import { HeavyServicesReadinessHelper } from './shared/helpers/heavy-services-readiness.helper';

// ============================================================================
// CONSTANTS - Magic numbers extracted for maintainability
// ============================================================================

/** Timeout for page reload after token exchange failure (milliseconds) */
const TOKEN_EXCHANGE_TIMEOUT_RELOAD_DELAY = 100;

/** Timeout for waiting for app context (projectId, workflowId, cacheId) in milliseconds */
const APP_CONTEXT_WAIT_TIMEOUT = 60000; // 60 seconds

/**
 * Backstop for a genuinely hung runtime init: after this long the readiness
 * gate opens regardless, so a Run button can never wait forever (SCRUM-749).
 *
 * Raised from 20s to 90s for EHQ-91. The gate is now a real barrier - before,
 * a transposed-argument bug made waitForReadiness() fall through after ~600ms,
 * so tripping this early was harmless. It no longer is: firing it mid-download
 * would let step code run against a half-loaded Pyodide. 90s comfortably
 * exceeds a cold ~27MB runtime fetch on a slow link while still bounding a hang.
 */
const HEAVY_SERVICES_MAX_WAIT_MS = 90000; // 90 seconds

/**
 * Fallback delay for {@link afterFirstPaint} when requestAnimationFrame never
 * fires (hidden tab). Long enough that a visible tab always wins the race via
 * rAF, short enough that a background tab still preloads promptly.
 */
const AFTER_FIRST_PAINT_FALLBACK_MS = 500;

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

/** Interface for services that support initialization */
interface InitializableService {
  init(): Promise<void>;
}

/** Result of a service initialization attempt */
interface ServiceInitializationResult {
  name: string;
  success: boolean;
  duration: string;
}

/** Configuration for a service to be initialized */
interface ServiceConfig {
  service: InitializableService | ServiceType;
  name: string;
}

/**
 * Union type representing services that can be initialized during app startup.
 * Note: This is a transitional type. Ideally all these services should implement
 * InitializableService interface for better type safety.
 */
type ServiceType =
  | AuthenticationService
  | PermissionsService
  | JavascriptService
  | PythonService
  | DuckDbService
  | Sam2WebsocketService
  | NotificationWebsocketService;

// Initialize authentication and check if user is authenticated
async function initializeAuthentication(
  authService: AuthenticationService,
): Promise<boolean> {
  console.log('🔐 Starting auth initialization...');
  await authService.initialize();
  console.log('🔐 Auth initialization completed');

  const isAuthenticated = authService.isAuthenticated();
  console.log('🔐 Is authenticated after init:', isAuthenticated);

  return isAuthenticated;
}

// Load user permissions and roles
async function initializePermissions(
  permissionsService: PermissionsService,
): Promise<void> {
  console.log('🔐 Loading permissions and roles...');
  await Promise.all([
    firstValueFrom(permissionsService.getRoles()),
    firstValueFrom(permissionsService.getPermissions()),
  ]);
  console.log('🔐 Permissions and roles loaded successfully');
}

// Schedule the execution runtimes to load in the background, after first paint
function scheduleHeavyServicesInitialization(
  appState: AppStateService,
  jsService: ServiceType,
  pythonService: ServiceType,
  duckDbService: ServiceType,
  sam2Service: ServiceType,
): void {
  // Only services Python step execution truly depends on gate the readiness
  // signal. SAM2 is optional/deprecated and must never block startup or the
  // first Python step (SCRUM-749) - kick its init off as fire-and-forget.
  void initializeSingleService(sam2Service, '🔌 Sam2WebsocketService').catch(
    (error) => {
      console.warn(
        '⚠️ Sam2WebsocketService init failed (non-blocking):',
        error,
      );
    },
  );

  // DuckDB joins Python and JS here (EHQ-91). It used to be a PHASE-1 blocking
  // service, which put a ~6.6MB wasm download ahead of first paint even though
  // nothing in the initial workflow render uses it - the only consumers are
  // PythonService and JavascriptService, i.e. code execution.
  initializeHeavyServicesInBackground(appState, [
    { service: jsService, name: '⚡ JavascriptService' },
    { service: pythonService, name: '🐍 PythonService' },
    { service: duckDbService, name: '🦆 DuckDbService' },
  ]).catch((error) => {
    console.error('❌ Execution runtime initialization failed:', error);
  });
}

/**
 * Resolve once the browser has painted, so the runtime downloads never compete
 * with the initial render for bandwidth (EHQ-91).
 *
 * Never gate solely on requestAnimationFrame: rAF is suspended while a tab is
 * hidden, and this is the only path that reaches BOTH markBootstrapComplete()
 * and markHeavyServicesReady(). A page opened in a background tab (open-in-new-
 * tab, restored session) would otherwise never preload the runtimes and would
 * leave the Run controls stuck showing "preparing". So race rAF against a timer
 * fallback - background timers are throttled but, unlike rAF, still fire.
 */
export function afterFirstPaint(): Promise<void> {
  if (
    typeof window === 'undefined' ||
    typeof requestAnimationFrame !== 'function'
  ) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    requestAnimationFrame(() => {
      // rAF fires just *before* paint; a nested task defers us to just after.
      setTimeout(finish, 0);
    });

    // Hidden-tab fallback (see above).
    setTimeout(finish, AFTER_FIRST_PAINT_FALLBACK_MS);
  });
}

// Unified initializer with smart background services initialization
const initializeAuthAndServices = async (): Promise<void> => {
  // ALL inject() calls MUST be at the top level of the function
  const authService: ServiceType = inject(AuthenticationService);
  const permissionsService: ServiceType = inject(PermissionsService);
  const deploymentInfoService = inject(DeploymentInfoService);
  const appState = inject(AppStateService);
  const pythonService: ServiceType = inject(PythonService);
  const jsService: ServiceType = inject(JavascriptService);
  const duckDbService: ServiceType = inject(DuckDbService);
  const sam2Service: ServiceType = inject(Sam2WebsocketService);
  const notificationService: ServiceType = inject(NotificationWebsocketService);
  const iconRegistry = inject(IconRegistryService);

  // Register all SVG icons immediately (synchronous, fast ~5-10ms)
  // This ensures icons are registered once globally before any components load
  iconRegistry.registerAllIcons();

  try {
    // 1. Auth and permissions - blocking (fast ~1-2 sec)
    const isAuthenticated = await initializeAuthentication(authService);

    if (isAuthenticated) {
      await initializePermissions(permissionsService);

      // Deployment-wide bootstrap info (SCRUM-782). Non-critical: a failure must
      // not block startup, so swallow errors and fall back to signal defaults.
      // Refine the Sentry environment from the SERVER's deploymentId (Sentry
      // initialized pre-auth in main.ts with the build-time default, which
      // mislabels local-ehq-web-against-dev); pre-login errors keep that default.
      try {
        const deploymentInfo = await deploymentInfoService.load();
        if (deploymentInfo?.deploymentId) {
          Sentry.setTag('environment', deploymentInfo.deploymentId);
        }
      } catch (error) {
        console.warn('⚠️ deployment-info load failed (non-critical):', error);
      }

      // PHASE 1: Critical services (blocking). Only things the initial render
      // genuinely needs belong here - execution runtimes do not (EHQ-91).
      await initializeCriticalServices([
        { service: notificationService, name: '📢 NotificationService' },
      ]);

      console.log('✅ UI unblocked - critical services ready');

      // PHASE 2: Execution runtimes (background, after paint, non-blocking)
      void afterFirstPaint().then(() => {
        scheduleHeavyServicesInitialization(
          appState,
          jsService,
          pythonService,
          duckDbService,
          sam2Service,
        );
      });
    } else {
      console.log('🔐 User not authenticated, skipping service initialization');
    }
  } catch (error) {
    console.error('🔐 Auth initialization failed:', error);

    if ((error as Error)?.message === 'Token exchange timeout') {
      console.log('Token exchange timeout - reloading page');
      setTimeout(
        () => window.location.reload(),
        TOKEN_EXCHANGE_TIMEOUT_RELOAD_DELAY,
      );
    }
  }
};

// ============================================================================
// HELPER FUNCTIONS - Service Initialization
// ============================================================================

/**
 * Initialize a single service and return result with timing information.
 * @param service - Service instance with init() method
 * @param name - Human-readable name for logging
 * @returns Result object with name, success status, and duration
 */
async function initializeSingleService(
  service: InitializableService | ServiceType,
  name: string,
): Promise<ServiceInitializationResult> {
  const startTime = performance.now();
  console.log(`${name} initializing...`);

  try {
    // Type assertion: We know all ServiceType instances have an init() method
    await (service as InitializableService).init();
    const duration = ((performance.now() - startTime) / 1000).toFixed(2);
    console.log(`${name} initialized successfully in ${duration}s`);
    return { name, success: true, duration };
  } catch (error) {
    const duration = ((performance.now() - startTime) / 1000).toFixed(2);
    console.error(`${name} failed after ${duration}s:`, error);
    throw new Error(`${name} failed after ${duration}s: ${error}`);
  }
}

/**
 * Initialize multiple services in parallel and report results.
 * @param services - Array of service configurations to initialize
 * @param label - Label for logging (e.g., "Critical services", "Heavy services")
 */
async function initializeServices(
  services: ServiceConfig[],
  label: string,
): Promise<void> {
  console.log(`⚡ Starting ${label.toLowerCase()} initialization...`);

  const results = await Promise.allSettled(
    services.map(({ service, name }) => initializeSingleService(service, name)),
  );

  const successful = results.filter((r) => r.status === 'fulfilled').length;
  const failed = results.filter((r) => r.status === 'rejected').length;
  console.log(`✅ ${label}: ${successful} successful, ${failed} failed`);

  // Detailed report on failures
  results.forEach((result, index) => {
    if (result.status === 'rejected') {
      console.error(`❌ ${services[index].name} failed:`, result.reason);
    }
  });
}

/**
 * Initialize critical services (blocking).
 * These are services required for basic app functionality.
 */
async function initializeCriticalServices(
  services: ServiceConfig[],
): Promise<void> {
  await initializeServices(services, 'Critical services');
}

// Wait for app context to be ready (projectId, workflowId, cacheId)
export async function waitForAppContext(
  appState: AppStateService,
): Promise<boolean> {
  console.log('⏳ Waiting for app context (projectId, workflowId, cacheId)...');

  const contextReady = await firstValueFrom(
    race([
      appState.contextReady$,
      timer(APP_CONTEXT_WAIT_TIMEOUT).pipe(map(() => false)),
    ]),
  );

  if (contextReady) {
    console.log('✅ Context ready for heavy services:', {
      projectId: appState.projectId(),
      workflowId: appState.workflowId(),
      cacheId: appState.cacheId(),
    });
  } else {
    console.warn(
      '⚠️ Timeout waiting for context, initializing heavy services anyway',
    );
  }

  // Mark bootstrap complete on BOTH paths. The readiness gate is
  // `heavyServices && bootstrap`, and this is the only place bootstrap is
  // marked - skipping it on timeout would leave the gate permanently shut, so
  // Run controls would stay disabled forever and every waitForReadiness() would
  // burn its full timeout. Harmless while the gate was a no-op; not anymore.
  // Proceeding here matches the long-standing "initialize heavy services
  // anyway" behaviour on timeout.
  HeavyServicesReadinessHelper.markBootstrapComplete();

  return contextReady;
}

/**
 * Initialize heavy services in background (non-blocking).
 * Waits for app context before initializing services.
 */
async function initializeHeavyServicesInBackground(
  appState: AppStateService,
  services: ServiceConfig[],
): Promise<void> {
  console.log('🐢 Starting heavy services in background...');

  try {
    // Wait for context readiness
    await waitForAppContext(appState);

    // Parallel initialization of heavy services, capped by a max-wait so a
    // stuck dependency cannot block the readiness gate (SCRUM-749).
    console.log('🐢 Starting parallel heavy services initialization...');
    let timedOut = false;
    let safetyNetTimer: ReturnType<typeof setTimeout> | undefined;
    await Promise.race([
      initializeServices(services, 'Heavy services'),
      new Promise<void>((resolve) => {
        safetyNetTimer = setTimeout(() => {
          timedOut = true;
          resolve();
        }, HEAVY_SERVICES_MAX_WAIT_MS);
      }),
    ]);
    // Always clear the safety-net timer so it cannot fire (and mutate
    // `timedOut`) after the race already resolved on the happy path.
    if (safetyNetTimer !== undefined) {
      clearTimeout(safetyNetTimer);
    }

    if (timedOut) {
      console.warn(
        `⚠️ Heavy services did not finish within ${HEAVY_SERVICES_MAX_WAIT_MS}ms - marking ready anyway to unblock app`,
      );
    }

    // Mark heavy services as ready (even if some failed - services should handle their own fallbacks)
    HeavyServicesReadinessHelper.markHeavyServicesReady();
  } catch (error) {
    console.error('❌ Critical error in heavy services initialization:', error);
    // Ensure the readiness gate never stays closed because of an unexpected throw.
    HeavyServicesReadinessHelper.markHeavyServicesReady();
  }
}

/**
 * EHQ-163: children of `project/:projectId` must be able to read `projectId`.
 *
 * Angular's default `'emptyOnly'` passes a parent's params down only to
 * empty-path children. `project/:projectId` has three children that all load
 * ProjectPageComponent — `''`, `workflow/:workflowId`, and
 * `workflow/:workflowId/step/:stepId` — so only the first one inherited it.
 * On the other two `route.paramMap.get('projectId')` returned null, and both
 * project sidebars lost their data: Steps rendered "No steps added yet" and
 * the project-info loaders bailed at their `if (!this.projectId) return`.
 * The project guard preserves the `/workflow/:workflowId` path segment when it
 * appends the workflowId query param, so that URL is what lands in the address
 * bar and breaks on every subsequent reload or paste.
 *
 * Exported so app.routes.spec.ts can exercise the real value against the real
 * route tree rather than a copy that can drift.
 */
export const routerConfig: ExtraOptions = {
  paramsInheritanceStrategy: 'always',
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withComponentInputBinding(),
      withEnabledBlockingInitialNavigation(),
      withRouterConfig(routerConfig),
    ),
    provideAnimations(),
    // CSP nonce for Angular inline styles and CDK overlays
    {
      provide: CSP_NONCE,
      useFactory: () => {
        // SSR guard: document is undefined on the server
        if (typeof document === 'undefined') return null;
        const meta = document.querySelector('meta[name="csp-nonce"]');
        const value = meta?.getAttribute('content');
        // Skip unreplaced placeholder (local dev / SSR without set-env.sh)
        return value && !value.includes('PLACEHOLDER') ? value : null;
      },
    },
    // Explicitly provide storage services
    LocalforageService,
    // Provide injection tokens to resolve circular dependencies
    { provide: APP_STATE_SERVICE, useExisting: AppStateService },
    { provide: PROJECT_SERVICE, useExisting: ProjectService },
    importProvidersFrom(
      AuthModule,
      ToastrModule.forRoot({
        timeOut: 5000,
        positionClass: 'toast-top-right',
        preventDuplicates: true,
        closeButton: true,
        progressBar: true,
      }),
      // ngx-bootstrap 21.2 dropped forRoot(); defaults come from the
      // root-provided *Config injectables, so import the modules directly.
      // Only alert and modal are actually used -- see EHQ-171. Tooltips are
      // Material (matTooltip); the dropdown-* classes in templates are
      // Bootstrap CSS, not BsDropdownModule directives.
      AlertModule,
      ModalModule,
    ),
    provideHttpClient(
      // Angular 22 flipped provideHttpClient()'s default backend from XHR to
      // fetch. Pinned to XHR deliberately so the Angular 22 upgrade does not
      // also change HTTP transport: fetch reports errors and aborts
      // differently, and every request here passes through the four
      // interceptors below.
      //
      // This is a deferral, not a preference — Angular deprecates XHR support
      // with intent to remove it in v23. EHQ-186 covers moving to fetch and
      // re-verifying the interceptor chain. Nothing here needs XHR
      // specifically: there are no reportProgress/HttpEventType usages, and
      // the X-Proxy-Status path in file.service.ts uses raw fetch already.
      withXhr(),
      withInterceptors([
        assetCacheInterceptor, // Must be first to cache assets before other interceptors
        tokenInterceptor,
        errorInterceptor,
        loadingInterceptor,
      ]),
    ),
    // Single unified initializer ensures proper sequential execution
    provideAppInitializer(initializeAuthAndServices),
  ],
};
